import { defaultStringifySearch } from "@tanstack/react-router"
import { NotificationKindEnum, PushMsgList } from "./notification.types"
import type { CompletionAlert } from "./completion-alerts"

export const getCompletionNotificationMessageKey = (
  kind: CompletionAlert["kind"]
) =>
  kind === "cat-review"
    ? "notification.catReviewCompleted"
    : kind === "review"
      ? "notification.documentReviewCompleted"
      : "notification.qualityEvaluationCompleted"

export const DEFAULT_NOTIFICATION_FORM: PushMsgList[] = [
  {
    createDt: "",
    id: "",
    isExisting: true,
    isRead: false,
    isUpdated: false,
    kind: NotificationKindEnum.ProjectInvoiceAdded,
    parameters: { id: "" },
    accountId: "",
  },
]

export const getNotificationMessageKey = (kind: NotificationKindEnum) => {
  switch (kind) {
    case NotificationKindEnum.CommentCreated:
      return "notification.catCommentCreated"
    case NotificationKindEnum.CommentResolved:
      return "notification.catCommentResolved"
    case NotificationKindEnum.CATWorkflowAssigned:
      return "notification.catWorkflowAssigned"
    case NotificationKindEnum.ProjectInvoiceAdded:
    default:
      return "notification.quotationCompletedNoti"
  }
}

export const getNotificationTargetPath = (
  message: PushMsgList,
  isAdmin: boolean
): string | null => {
  if (message.kind === NotificationKindEnum.ProjectInvoiceAdded) {
    const orderId = message.parameters?.id
    return orderId
      ? `/orders/${encodeURIComponent(orderId)}${isAdmin ? "/manage" : ""}`
      : null
  }

  // 배정 알림은 편집기가 아니라 할당 목록으로 보낸다. 배정은 이제 초대이고, 답하기 전에는
  // 워크스페이스가 열리지 않는다 — 편집기로 보내면 알림을 따라간 사람이 곧장 권한 오류를 만나고
  // 수락 버튼은 그 사람이 안 가본 화면에 있다.
  if (message.kind === NotificationKindEnum.CATWorkflowAssigned) {
    // 어느 초대인지도 실어 보낸다. 목록은 오프셋 페이지라 카드가 첫 페이지에 없을 수 있고, 있어도
    // 어느 카드인지 표시가 없었다. jobId 가 없어도 목록 자체는 갈 곳이므로 그대로 보낸다.
    // 쿼리는 라우터의 인코딩으로 쓴다. 라우터는 검색값마다 JSON.parse 를 돌리므로, 숫자만으로 된
    // id 를 `?jobId=7412345678901234567` 로 실으면 라우트에는 Number 로 닿고(u64 SnowFlake 는
    // 2^53 을 넘어 자릿수부터 잃는다) 라우트의 z.string() 이 버린다. defaultStringifySearch 는
    // 그런 값을 따옴표로 감싸 보내(`?jobId=%22…%22`) 파서가 문자열 그대로 돌려주게 한다.
    const assignedJobId = message.parameters?.jobId
    return `/translations/assigned${
      assignedJobId ? defaultStringifySearch({ jobId: assignedJobId }) : ""
    }`
  }

  const jobId = message.parameters?.jobId
  if (!jobId) return null

  const search = new URLSearchParams()
  if (message.parameters?.segmentId) {
    search.set("segmentId", message.parameters.segmentId)
  }
  if (message.parameters?.commentId) {
    search.set("commentId", message.parameters.commentId)
  }
  const query = search.toString()
  return `/translations/jobs/${encodeURIComponent(jobId)}/editor${query ? `?${query}` : ""}`
}
